// @fileoverview This composable is used to handle GIF playback with respect to reduced motion preferences.
// It automatically extracts the first frame of the GIF for use as a static preview when the GIF is paused
// or when reduced motion is enabled.

import { useSurfaceAttachmentsStore } from '@@/pinia/surface_attachments'
import type { User } from '@@/types'
import { useReducedMotionMode } from '@@/vuecomposables/useReducedMotionMode'
import { computedAsync } from '@vueuse/core'
import type { Ref } from 'vue'
import { ref, watch } from 'vue'

interface UseGifWithReducedMotionOptions {
  /**
   * The URL of the GIF image
   */
  imageUrl: Ref<string | null | undefined>
  /**
   * The current user, used to determine reduced motion preferences
   */
  currentUser: Ref<User | null>
  /**
   * Whether the image is actually a GIF. If false, reduced motion handling is skipped
   */
  isGif: Ref<boolean>
}

interface UseGifWithReducedMotionReturn {
  /**
   * Whether the GIF is currently paused
   */
  isGifPaused: Ref<boolean>
  /**
   * The data URL of the first frame, used when the GIF is paused
   */
  firstFrameDataUrl: Ref<string>
  /**
   * Toggle the GIF pause state
   */
  toggleGifPause: () => void
}

/**
 * Composable for handling GIF playback with respect to reduced motion preferences.
 * Automatically extracts the first frame of the GIF for use as a static preview
 * when the GIF is paused or when reduced motion is enabled.
 *
 * @param options - Configuration options
 * @returns Object containing pause state, first frame URL, and controls
 *
 * @example
 * ```vue
 * <script setup lang="ts">
 * const { isGifPaused, firstFrameDataUrl, toggleGifPause } = useGifWithReducedMotion({
 *   imageUrl,
 *   isGif,
 *   currentUser,
 * })
 * </script>
 *
 * <template>
 *   <img v-if="isGifPaused && firstFrameDataUrl" :src="firstFrameDataUrl" />
 *   <img v-else :src="imageUrl" />
 *   <button @click="toggleGifPause">Toggle</button>
 * </template>
 * ```
 */
export function useGifWithReducedMotion({
  imageUrl,
  isGif,
  currentUser,
}: UseGifWithReducedMotionOptions): UseGifWithReducedMotionReturn {
  const surfaceAttachmentsStore = useSurfaceAttachmentsStore()
  const { getFirstFrameUrlForImageUrl } = surfaceAttachmentsStore

  const isGifPaused = ref(false)

  const { isInReducedMotionMode } = useReducedMotionMode(currentUser)

  const firstFrameDataUrl = computedAsync(async () => {
    if (imageUrl.value != null && isGif.value) {
      return (await getFirstFrameUrlForImageUrl(imageUrl.value)) ?? ''
    }
    return ''
  }, '')

  watch(
    [isGif, isInReducedMotionMode],
    ([isGifImage, isInReducedMotion]) => {
      if (isGifImage) {
        isGifPaused.value = !!isInReducedMotion
      }
    },
    { immediate: true },
  )

  const toggleGifPause = (): void => {
    isGifPaused.value = !isGifPaused.value
  }

  return {
    isGifPaused,
    firstFrameDataUrl,
    toggleGifPause,
  }
}
